export function format(first: string, middle: string, last: string): string {
  return (first || '') + (middle ? ` ${middle}` : '') + (last ? ` ${last}` : '');
}

/* Smooth-streaming reveal pacing.
 *
 * Streamed answers arrive in a few large websocket frames on fast models,
 * which kills the typewriter feel. The reveal loop drains a backlog of
 * not-yet-shown characters at a rate that grows with the backlog, so the
 * display never lags the stream by more than ~REVEAL_MAX_LAG_S seconds but
 * still flows evenly when the backlog is small.
 */

// Baseline reveal speed in characters/second when there is little backlog.
export const REVEAL_BASE_CPS = 140;
// Ceiling on how far (in seconds at the current rate) the display may lag
// behind what the server has already sent.
export const REVEAL_MAX_LAG_S = 1.2;
// Faster ceiling once the stream has completed — drain what's left quickly
// so sources/feedback aren't held back noticeably.
export const REVEAL_DRAIN_S = 0.4;

/** Characters to reveal this frame. ``carry`` accumulates fractional
 * characters across frames so low rates still advance; returns the chars to
 * reveal plus the new carry. */
export function revealStep(
  backlogChars: number,
  dtSeconds: number,
  carry: number,
  maxLagSeconds: number = REVEAL_MAX_LAG_S
): { chars: number; carry: number } {
  if (backlogChars <= 0) return { chars: 0, carry: 0 };
  const rate = Math.max(REVEAL_BASE_CPS, backlogChars / maxLagSeconds);
  const budget = carry + rate * dtSeconds;
  const chars = Math.min(Math.floor(budget), backlogChars);
  return { chars, carry: chars >= backlogChars ? 0 : budget - chars };
}

/** Avoid ending a reveal slice between the halves of a surrogate pair, which
 * would transiently render a replacement character. */
export function extendPastSurrogate(text: string, end: number): number {
  const code = text.charCodeAt(end - 1);
  if (code >= 0xd800 && code <= 0xdbff && end < text.length) return end + 1;
  return end;
}

/** When a slice ends at the very end of the backlog on a high surrogate, the
 * low half hasn't streamed in yet and extending is impossible; retreat one
 * character so the pair is revealed together once its second half arrives. */
export function retreatBeforeTrailingSurrogate(text: string, end: number): number {
  if (end !== text.length) return end;
  const code = text.charCodeAt(end - 1);
  if (code >= 0xd800 && code <= 0xdbff) return end - 1;
  return end;
}
